<template>
  <strong
    :class="[
      'lottery-extra flex items-center w-fit gap-200 py-050 pr-050 pl-100 bg-secondary-secondary rounded-xl',
      {
        'lottery-extra-active': isExtraHitEnabled,
      },
    ]"
  >
    <div class="flex items-center gap-100">
      <LcIcon
        class="lottery-extra-icon"
        :class="{
          'lottery-extra-icon-filled': isExtraHitEnabled,
          'lottery-extra-icon-animate': isIconAnimating,
        }"
        :name="extraHitIcon"
        size="medium"
        @animationend="isIconAnimating = false"
      />
      <span class="lottery-extra-text">
        {{ $t(`lottery.${lotteryId}.extrahit`) }}
      </span>
      <LcInfoButton
        :key="`infobutton_${locale}`"
        :headline="$t(`lottery.${lotteryId}.extrahit.info.headline`)"
        :open-button-aria-label="$t(`lottery.${lotteryId}.extrahit.info.label`)"
        :text="$t(`lottery.${lotteryId}.extrahit.info.message`)"
      />
    </div>

    <LcSwitch
      :aria-label-text="$t(`lottery.${lotteryId}.extrahit`)"
      :value="isExtraHitEnabled"
      icon="checkmark"
      name="switch"
      variant="success"
      @update:value="handleToggleExtraHit"
    />
  </strong>
</template>

<script lang="ts" setup>
import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { Config } from '../@types/editor'

import { type EditorPlay, useNumberLotteryStore } from '../stores/numberLottery'

type Props = {
  config: Config
  lotteryId: LotteryId
  play: EditorPlay
}

const { config, lotteryId, play } = defineProps<Props>()

const { locale } = useI18n()
const store = useNumberLotteryStore(lotteryId)
const { extraBallBg, extraHitIcon } = useNumberLotteryTheme(lotteryId)

const { isAnimating } = useEditorQuickPickAnimation()

const isIconAnimating = ref(false)

const isExtraHitEnabled = computed(() => store.isExtraHitEnabled(play))

watch(isExtraHitEnabled, (enabled) => {
  if (enabled) isIconAnimating.value = true
})

const handleToggleExtraHit = () => {
  if (isAnimating(play.id)) return
  store.toggleExtraHit(play.id, config)
}
</script>

<style lang="scss" scoped>
.lottery-extra {
  position: relative;
  isolation: isolate;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    border-radius: inherit;
    background-image: url('../assets/lottery-extra-background.svg');
    background-position: right;
    background-repeat: no-repeat;
    opacity: 0;
    pointer-events: none;
    transition: opacity $transition-duration-pop $transition-ease-pop;
  }
}

.lottery-extra-active {
  &::after {
    opacity: 1;
  }
}

.lottery-extra-icon-animate {
  @include animation-lottery-remaining-picks-pop;
}

.lottery-extra-icon {
  border: token('size/border/100') solid token('color/icon/primary/primary');
  border-radius: token('size/radius/full');
}

.lottery-extra-icon-filled {
  border-color: v-bind(extraBallBg);
  background-color: v-bind(extraBallBg);
  color: token('color/icon/primary/primary-inverted');
}

.lottery-extra-text {
  @include token-caption-bold;
}
</style>
